import React from 'react';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faArrowRight } from '@fortawesome/free-solid-svg-icons';
import { useIntl } from 'react-intl';
import { ConfirmDialogWithTextInput } from 'app/components';
import { TranslationKeys } from 'app/translations';
import type { BulkLeftCompanyEmployee, BulkLeftCompanyBackupResponse } from '../../../Services';
import { BackupPreview } from './BackupPreview';

const CONFIRM_SOLUTION = 'CONFIRM';

type ReviewStepProps = {
  backupData: BulkLeftCompanyBackupResponse;
  employees: BulkLeftCompanyEmployee[];
  isSubmitting: boolean;
  isConfirmOpen: boolean;
  setIsConfirmOpen: (open: boolean) => void;
  onOpenConfirm: () => void;
  onConfirm: () => void;
  onReset: () => void;
};

export const ReviewStep = ({
  backupData,
  employees,
  isSubmitting,
  isConfirmOpen,
  setIsConfirmOpen,
  onOpenConfirm,
  onConfirm,
  onReset,
}: ReviewStepProps) => {
  const intl = useIntl();

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        {intl.formatMessage({ id: TranslationKeys.adminPanel_bulkLeftCompany_preview_title })}
      </Typography>

      <BackupPreview data={backupData} csvEmployees={employees} />

      <Box sx={{ mt: 4, display: 'flex', justifyContent: 'space-between' }}>
        <Button variant="outlined" startIcon={<FontAwesomeIcon icon={faArrowLeft} />} onClick={onReset}>
          {intl.formatMessage({ id: TranslationKeys.adminPanel_bulkLeftCompany_step_upload })}
        </Button>

        <Button
          variant="contained"
          color="warning"
          disabled={isSubmitting}
          endIcon={
            isSubmitting ? <CircularProgress size={16} color="inherit" /> : <FontAwesomeIcon icon={faArrowRight} />
          }
          onClick={onOpenConfirm}
        >
          {intl.formatMessage({ id: TranslationKeys.adminPanel_bulkLeftCompany_submit })}
        </Button>
      </Box>

      <ConfirmDialogWithTextInput
        open={isConfirmOpen}
        setOpen={setIsConfirmOpen}
        title={intl.formatMessage({ id: TranslationKeys.adminPanel_bulkLeftCompany_confirmDialog_title })}
        solution={CONFIRM_SOLUTION}
        label={intl.formatMessage({ id: TranslationKeys.adminPanel_bulkLeftCompany_confirmDialog_inputLabel })}
        errorMessage={intl.formatMessage({
          id: TranslationKeys.adminPanel_bulkLeftCompany_confirmDialog_inputError,
        })}
        onConfirm={onConfirm}
      >
        <Typography variant="body2">
          {intl.formatMessage({ id: TranslationKeys.adminPanel_bulkLeftCompany_confirmDialog_content })}
        </Typography>
      </ConfirmDialogWithTextInput>
    </Box>
  );
};
